/**
 * Send a templated message.
 *
 * Templates only. The free text box is a variable inside a template that already carries the
 * compliance footer, not a replacement for one, which is why the note field feeds
 * `variables.note` rather than the body.
 *
 * The send holds for sixty seconds by default and says so on the button, because the undo is
 * the only thing that makes a wrong send recoverable.
 */
import { useState } from 'react';
import { buttonStyle } from './temp-ui';
import { useMessageTemplates, useSendMessage } from './api';

export const DEFAULT_HOLD_SECONDS = 60;

export function SendMessageDialog({
  customerKey,
  channel,
  onClose,
}: {
  customerKey: string;
  channel: 'email' | 'sms';
  onClose: () => void;
}) {
  const templates = useMessageTemplates();
  const send = useSendMessage(customerKey);
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const usable = (templates.data?.items ?? []).filter((template) => template.channels.includes(channel));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Send ${channel}`}
      style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', background: 'var(--shadow-scrim)' }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          setError(null);
          send.mutate(
            {
              channel,
              template: templateId,
              variables: {},
              note: String(data.get('note') ?? '') || null,
              reason: String(data.get('reason') ?? ''),
              hold_seconds: DEFAULT_HOLD_SECONDS,
            },
            {
              onSuccess: () => onClose(),
              onError: (err: unknown) => setError(err instanceof Error ? err.message : 'The send was refused.'),
            },
          );
        }}
        style={{
          background: 'var(--surface-1)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--card-padding)',
          width: 'min(560px, 92vw)',
        }}
      >
        <h2 style={{ marginTop: 0 }}>Send {channel === 'sms' ? 'an SMS' : 'an email'}</h2>

        <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
          <span className="caps-label">Template</span>
          <select
            aria-label="Template"
            required
            value={templateId}
            onChange={(event) => setTemplateId(event.currentTarget.value)}
            style={{ display: 'block', width: '100%', padding: 'var(--space-2)' }}
          >
            <option value="">Choose a template</option>
            {usable.map((template) => (
              <option key={template.id} value={template.id}>
                {template.label}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
          <span className="caps-label">Note (fills the template note variable)</span>
          <textarea name="note" rows={3} style={{ display: 'block', width: '100%', padding: 'var(--space-2)' }} />
        </label>

        <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
          <span className="caps-label">Reason</span>
          <input name="reason" required minLength={3} style={{ display: 'block', width: '100%', padding: 'var(--space-2)' }} />
        </label>

        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--type-meta)' }}>
          Caps: three emails and three SMS per customer per day. SMS outside 08:00 to 22:00 New York queues until
          morning.
        </p>

        {error ? (
          <p role="alert" style={{ color: 'var(--crit)' }}>
            {error}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button type="button" style={buttonStyle()} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" style={buttonStyle('primary')} disabled={send.isPending || templateId === ''}>
            Send (holds {DEFAULT_HOLD_SECONDS}s)
          </button>
        </div>
      </form>
    </div>
  );
}
